import { get } from 'lodash';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  type TextFieldProps,
} from '@mui/material';
import { DatePicker, type DatePickerProps } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import React from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { updateField } from 'store/SquaresStore/WorkOrderManagement/WorkOrderCreationFormState.slice';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);
dayjs.extend(timezone);

interface FormLabelProps {
  children: React.ReactNode;
  required?: boolean;
}

type MemoizedNumericTextFieldWithIconProps = Omit<TextFieldProps, 'name'> & {
  name: string;
  onIncrement: () => void;
  onDecrement: () => void;
};

type MemoizedTextFieldProps = Omit<
  TextFieldProps,
  'name' | 'value' | 'onChange'
> & {
  name: string;
  options?: Array<{ value: string | number; label: string }>; // For select fields
};

type MemoizedDatePickerProps = Omit<
  DatePickerProps<Dayjs>,
  'value' | 'onChange'
> & {
  name: string;
};

interface MemoizedTimeSelectorWithUnitComponentProps {
  name: string;
  label?: string;
  required?: boolean;
  value: number;
  unit: 'Hours' | 'Days';
  onValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUnitChange: (
    event: React.MouseEvent<HTMLElement>,
    newUnit: 'Hours' | 'Days' | null
  ) => void;
  onIncrement: () => void;
  onDecrement: () => void;
  error?: string;
}

export const FormLabel: React.FC<FormLabelProps> = ({
  children,
  required = false,
}) => (
  <Typography
    variant="body2"
    sx={{ mb: 1, fontWeight: 500 }}
    color="text.secondary"
  >
    {children}
    {required && <span style={{ color: 'red' }}>*</span>}
  </Typography>
);

// reusable wrapper for internal use
const MemoizedNumericTextFieldWithIconComponent: React.FC<
  MemoizedNumericTextFieldWithIconProps
> = ({ name, value, onChange, onIncrement, onDecrement, ...rest }) => {
  const error = useAppSelector((state) =>
    get(state.sqauresWorkOrderCreationFormReducer.errors, name, '')
  );
  return (
    <TextField
      name={name}
      type="number"
      fullWidth
      size="small"
      value={value ?? ''}
      onChange={onChange}
      onWheel={(e) => {
        (e.target as HTMLInputElement)?.blur();
      }}
      onKeyDown={(e) => {
        if (['e', 'E', '.', '-', '+'].includes(e.key)) {
          e.preventDefault();
        }
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <IconButton onClick={onDecrement} size="small">
              <RemoveIcon />
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={onIncrement} size="small">
              <AddIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
      error={!!error}
      helperText={error}
      {...rest}
    />
  );
};

const MemoizedTextFieldComponent: React.FC<MemoizedTextFieldProps> = ({
  name,
  options,
  ...rest
}) => {
  const dispatch = useAppDispatch();

  const value = useAppSelector((state) =>
    get(state.sqauresWorkOrderCreationFormReducer.values, name, '')
  );

  const error = useAppSelector((state) =>
    get(state.sqauresWorkOrderCreationFormReducer.errors, name, '')
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateField({ key: name, value: event.target.value }));
  };

  return (
    <TextField
      name={name}
      value={value}
      onChange={handleChange}
      SelectProps={{ MenuProps: { sx: { maxHeight: '400px' } } }}
      {...rest}
      error={!!error}
      helperText={error}
    >
      {options?.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

const MemoizedDatePickerComponent: React.FC<MemoizedDatePickerProps> = ({
  name,
  ...rest
}) => {
  const dispatch = useAppDispatch();

  const timezoneBasedOnDisplayMode =
    useTimeDisplayMode()?.timezoneBasedOnDisplayMode || dayjs.tz.guess();

  const value = useAppSelector(
    (state) =>
      get(state.sqauresWorkOrderCreationFormReducer.values, name) as
        | string
        | null
  );
  const error = useAppSelector((state) =>
    get(state.sqauresWorkOrderCreationFormReducer.errors, name, '')
  );

  const utcDate = value
    ? dayjs.utc(value).tz(timezoneBasedOnDisplayMode)
    : null;

  const handleChange = (date: Dayjs | null) => {
    const utcDate = dayjs(date).tz(timezoneBasedOnDisplayMode).utc();

    dispatch(
      updateField({ key: name, value: date ? utcDate.toISOString() : null })
    );
  };

  return (
    <DatePicker
      value={utcDate ?? null}
      onChange={handleChange}
      timezone={timezoneBasedOnDisplayMode}
      {...rest}
      slotProps={{
        textField: {
          size: 'small',
          fullWidth: true,
          error: !!error,
          helperText: error,
        },
      }}
    />
  );
};

const MemoizedTimeSelectorWithUnitComponent: React.FC<
  MemoizedTimeSelectorWithUnitComponentProps
> = ({
  name,
  label,
  required = false,
  value,
  unit,
  onValueChange,
  onUnitChange,
  onIncrement,
  onDecrement,
  error,
}) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ width: '100%' }}>
      {label && <FormLabel required={required}>{label}</FormLabel>}
      <Box sx={{ display: 'flex', alignItems: 'start', gap: 3 }}>
        <MemoizedNumericTextFieldWithIconComponent
          name={name}
          value={value}
          onChange={onValueChange}
          onIncrement={onIncrement}
          onDecrement={onDecrement}
          error={!!error}
          helperText={error}
        />
        <ToggleButtonGroup
          value={unit}
          exclusive
          onChange={onUnitChange}
          size="small"
        >
          <ToggleButton value="Hours">{t('hours')}</ToggleButton>
          <ToggleButton value="Days">Days</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
};

export const MemoizedDatePicker = React.memo(MemoizedDatePickerComponent);
export const MemoizedTextField = React.memo(MemoizedTextFieldComponent);
export const MemoizedTimeSelectorWithUnit = React.memo(
  MemoizedTimeSelectorWithUnitComponent
);
export const MemoizedNumericTextFieldWithIcon = React.memo(
  MemoizedNumericTextFieldWithIconComponent
);
